'use client';

// ---------------------------------------------------------------------------
// ScheduleTab — time-of-day windows
// ---------------------------------------------------------------------------
//
// The copy here carries a caveat the implementation cannot remove: a browser can
// only honour a schedule while the app is open. If everyone logs out, nothing
// plays. Saying so plainly is the difference between a feature and a complaint —
// a manager who sets "Breakfast 07:00" and hears silence at 07:00 because the
// front desk logged off needs to know that is expected, not broken.
//
// End earlier than start is legal and means the window wraps past midnight, so
// the form accepts 22:00 -> 02:00 and labels it.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { CalendarClock, Info, Plus, Power, Trash2 } from 'lucide-react';

import { useTranslation } from '@/core/i18n';
import { useToast } from '@/core/hooks/useToast';
import { cn } from '@/core/utils/cn';

import { useMusicPlaylists } from '../hooks/useMusicPlaylists';
import {
  daysToMask,
  formatMinutesAsTime,
  maskToDays,
  parseTimeToMinutes,
  wrapsMidnight,
} from '../lib/schedule';
import { useMusicStore } from '../stores/music.store';

/** Sunday-first, matching the bitmask where Sunday is bit 0. */
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export function ScheduleTab() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    playlists,
    schedules,
    timezone,
    unavailable,
    createSchedule,
    toggleSchedule,
    deleteSchedule,
    messageOf,
  } = useMusicPlaylists();

  const scheduleEnabled = useMusicStore((s) => s.scheduleEnabled);
  const setScheduleEnabled = useMusicStore((s) => s.setScheduleEnabled);
  const activeScheduleId = useMusicStore((s) => s.activeScheduleId);

  const [name, setName] = useState('');
  const [playlistId, setPlaylistId] = useState('');
  const [start, setStart] = useState('07:00');
  const [end, setEnd] = useState('10:30');
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);

  const startMinute = parseTimeToMinutes(start);
  const endMinute = parseTimeToMinutes(end);

  const canCreate =
    name.trim().length > 0 &&
    playlistId.length > 0 &&
    startMinute !== null &&
    endMinute !== null &&
    startMinute !== endMinute &&
    days.length > 0 &&
    !createSchedule.isPending;

  const submit = async () => {
    if (!canCreate || startMinute === null || endMinute === null) return;
    try {
      await createSchedule.mutateAsync({
        playlistId,
        name: name.trim(),
        daysMask: daysToMask(days),
        startMinute,
        endMinute,
      });
      toast('success', t('music.title'), t('music.scheduleCreated'));
      setName('');
    } catch (err) {
      toast('error', t('music.title'), messageOf(err, t('music.scheduleCreateFailed')));
    }
  };

  if (unavailable) {
    return <p className="px-1 text-[12px] text-hms-textDim">{t('music.playlistsNeedProperty')}</p>;
  }

  return (
    <div className="space-y-3">
      {/* The honest caveat, first. */}
      <p className="flex items-start gap-1.5 px-1 text-[10.5px] text-hms-textDim">
        <Info size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
        {t('music.scheduleCaveat')}
      </p>

      <label className="flex items-center gap-2 px-1 cursor-pointer">
        <input
          id="music-schedule-enabled"
          type="checkbox"
          checked={scheduleEnabled}
          onChange={(e) => setScheduleEnabled(e.target.checked)}
          className="accent-hms-teal"
        />
        <span className="text-[12px] text-hms-text">{t('music.followSchedule')}</span>
      </label>

      {timezone && (
        <p className="px-1 text-[10.5px] text-hms-textDim">
          {t('music.scheduleTimezone', { zone: timezone })}
        </p>
      )}

      {/* New window */}
      <section className="px-1 space-y-1.5">
        <p className="text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">
          {t('music.newWindow')}
        </p>

        {playlists.length === 0 ? (
          <p className="text-[12px] text-hms-textDim">{t('music.needPlaylistFirst')}</p>
        ) : (
          <>
            <input
              id="music-schedule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('music.windowNamePlaceholder')}
              className="w-full px-2.5 py-1.5 rounded-lg bg-hms-bg border border-hms-border text-[12.5px] text-hms-text placeholder:text-hms-textDim"
            />

            <select
              id="music-schedule-playlist"
              value={playlistId}
              onChange={(e) => setPlaylistId(e.target.value)}
              aria-label={t('music.choosePlaylist')}
              className="w-full px-2.5 py-1.5 rounded-lg bg-hms-bg border border-hms-border text-[12.5px] text-hms-text"
            >
              <option value="">{t('music.choosePlaylist')}</option>
              {playlists.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-1.5">
              <input
                id="music-schedule-start"
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                aria-label={t('music.startTime')}
                className="flex-1 px-2.5 py-1.5 rounded-lg bg-hms-bg border border-hms-border text-[12.5px] text-hms-text"
              />
              <span className="text-[11px] text-hms-textDim">–</span>
              <input
                id="music-schedule-end"
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                aria-label={t('music.endTime')}
                className="flex-1 px-2.5 py-1.5 rounded-lg bg-hms-bg border border-hms-border text-[12.5px] text-hms-text"
              />
            </div>

            {startMinute !== null && endMinute !== null && startMinute === endMinute && (
              <p className="text-[10.5px] text-hms-red">{t('music.sameTimeError')}</p>
            )}
            {startMinute !== null &&
              endMinute !== null &&
              wrapsMidnight({ startMinute, endMinute }) && (
                // Legal and intended — say so, or it looks like a mistake.
                <p className="text-[10.5px] text-hms-textDim">{t('music.wrapsMidnight')}</p>
              )}

            <div className="flex items-center gap-1">
              {DAY_KEYS.map((key, index) => {
                const on = days.includes(index);
                return (
                  <button
                    key={key}
                    id={`music-schedule-day-${key}`}
                    onClick={() =>
                      setDays((prev) =>
                        prev.includes(index) ? prev.filter((d) => d !== index) : [...prev, index],
                      )
                    }
                    aria-pressed={on}
                    aria-label={t(`music.days.${key}`)}
                    className={cn(
                      'flex-1 py-1 rounded-lg text-[10.5px] transition-all duration-200',
                      on
                        ? 'bg-hms-teal/15 text-hms-teal'
                        : 'text-hms-textSub hover:text-hms-text hover:bg-white/[0.04]',
                    )}
                  >
                    {t(`music.days.${key}`)}
                  </button>
                );
              })}
            </div>
            {days.length === 0 && (
              <p className="text-[10.5px] text-hms-red">{t('music.pickADay')}</p>
            )}

            <button
              id="music-schedule-create"
              onClick={() => void submit()}
              disabled={!canCreate}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-hms-teal/15 text-hms-teal text-[12px] hover:bg-hms-teal/25 disabled:opacity-30 transition-all duration-200"
            >
              <Plus size={13} strokeWidth={2} />
              {t('music.addWindow')}
            </button>
          </>
        )}
      </section>

      {/* Existing windows */}
      <section>
        <p className="px-1 pb-1 text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">
          {t('music.windows')}
        </p>

        {schedules.length === 0 ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.noWindows')}</p>
        ) : (
          <ul id="music-schedules-list">
            {schedules.map((s) => (
              <li
                key={s.id}
                className={cn(
                  'group flex items-center gap-2 px-1.5 py-1.5 rounded-lg transition-all duration-200',
                  s.id === activeScheduleId ? 'bg-hms-teal/10' : 'hover:bg-white/[0.04]',
                  !s.isActive && 'opacity-50',
                )}
              >
                <CalendarClock
                  size={13}
                  strokeWidth={2}
                  className={cn(
                    'flex-shrink-0',
                    s.id === activeScheduleId ? 'text-hms-teal' : 'text-hms-textDim',
                  )}
                />
                <div className="flex-1 min-w-0">
                  <p dir="auto" className="text-[12.5px] text-hms-text truncate">
                    {s.name}
                    {s.id === activeScheduleId && (
                      <span className="ms-1.5 text-[10px] text-hms-teal">
                        {t('music.playingNow')}
                      </span>
                    )}
                  </p>
                  <span className="text-[10.5px] text-hms-textDim">
                    {formatMinutesAsTime(s.startMinute)}–{formatMinutesAsTime(s.endMinute)} ·{' '}
                    {maskToDays(s.daysMask)
                      .map((d) => t(`music.days.${DAY_KEYS[d]}`))
                      .join(' ')}
                    {s.playlist ? ` · ${s.playlist.name}` : ''}
                  </span>
                </div>

                <button
                  onClick={() => {
                    void toggleSchedule
                      .mutateAsync({ id: s.id, isActive: !s.isActive })
                      .catch((err) =>
                        toast('error', t('music.title'), messageOf(err, t('music.saveFailed'))),
                      );
                  }}
                  aria-label={s.isActive ? t('music.turnOff') : t('music.turnOn')}
                  title={s.isActive ? t('music.turnOff') : t('music.turnOn')}
                  className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text flex-shrink-0 transition-all duration-200"
                >
                  <Power size={13} strokeWidth={2} />
                </button>

                <button
                  onClick={() => {
                    void deleteSchedule
                      .mutateAsync(s.id)
                      .catch((err) =>
                        toast('error', t('music.title'), messageOf(err, t('music.deleteFailed'))),
                      );
                  }}
                  aria-label={t('music.deleteWindow')}
                  title={t('music.deleteWindow')}
                  className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-red opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 transition-all duration-200"
                >
                  <Trash2 size={13} strokeWidth={2} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
